import {
  type Decimal,
  decimalToString,
  fromScaled,
  isZeroDecimal,
  toSignedScaled,
  trimDecimal,
} from "@/lib/tool-kit/arithmetic/decimal";
import {
  BIG_ONE,
  BIG_TWO,
  BIG_ZERO,
  modPowBig,
  pow10Big,
} from "@/lib/tool-kit/math/bigint";

/**
 * The remainder of a division, under the three conventions programming languages and
 * textbooks use when a number is negative.
 *
 * All three satisfy a = q·n + r; they differ in how the quotient q is rounded:
 *
 * - **Truncated** rounds q toward zero, so r takes the sign of the dividend a. This is the
 *   `%` operator of C, C++, Java, JavaScript, Go, Rust, PHP and Swift.
 * - **Floored** rounds q toward −∞, so r takes the sign of the divisor n. This is Python's
 *   and Ruby's `%`, Excel's MOD, and what a mathematician means for a positive n.
 * - **Euclidean** keeps 0 ≤ r < |n| whatever the signs — the least non-negative residue.
 *
 * Decimals are scaled to whole numbers first, so 5.5 mod 2 is exact.
 */
export type Convention = "euclidean" | "floored" | "truncated";

export const CONVENTIONS: readonly Convention[] = [
  "euclidean",
  "floored",
  "truncated",
];

export interface DivisionResult {
  convention: Convention;
  quotient: bigint;
  /** The remainder as a decimal, trailing zeros removed. */
  remainder: Decimal;
  /** The remainder as a scaled whole number (units of 10^−scale). */
  remainderScaled: bigint;
  scale: number;
}

function quotientFor(a: bigint, n: bigint, convention: Convention): bigint {
  const truncated = a / n;
  const remainder = a - truncated * n;
  if (convention === "truncated" || remainder === BIG_ZERO) {
    return truncated;
  }
  if (convention === "floored") {
    const differentSigns = remainder < BIG_ZERO !== n < BIG_ZERO;
    return differentSigns ? truncated - BIG_ONE : truncated;
  }
  // Euclidean: a negative remainder is moved up by |n|.
  if (remainder > BIG_ZERO) {
    return truncated;
  }
  return n > BIG_ZERO ? truncated - BIG_ONE : truncated + BIG_ONE;
}

/** a mod n under one convention; n must not be zero. */
export function remainderOf(
  a: Decimal,
  n: Decimal,
  convention: Convention
): DivisionResult | null {
  if (isZeroDecimal(n)) {
    return null;
  }
  const scale = Math.max(a.frac.length, n.frac.length);
  const sa = toSignedScaled(a);
  const sn = toSignedScaled(n);
  const A = sa.value * pow10Big(scale - sa.scale);
  const N = sn.value * pow10Big(scale - sn.scale);
  const quotient = quotientFor(A, N, convention);
  const remainderScaled = A - quotient * N;
  return {
    convention,
    quotient,
    remainder: trimDecimal(fromScaled(remainderScaled, scale)),
    remainderScaled,
    scale,
  };
}

export function remainderText(result: DivisionResult): string {
  return decimalToString(result.remainder);
}

/** Do the three conventions give the same remainder for these inputs? */
export function conventionsAgree(results: readonly DivisionResult[]): boolean {
  return results.every((r) => r.remainderScaled === results[0].remainderScaled);
}

/* ------------------------------------------------------ modular operations */

export type ModOperation = "add" | "subtract" | "multiply" | "power";

/** Least non-negative residue of x mod n, for n > 0. */
export function mod(x: bigint, n: bigint): bigint {
  const r = x % n;
  return r < BIG_ZERO ? r + n : r;
}

export interface ModularResult {
  operation: ModOperation;
  a: bigint;
  b: bigint;
  n: bigint;
  /** a and b reduced mod n. */
  aReduced: bigint;
  bReduced: bigint;
  /** The exact result before reducing (not for powers). */
  raw: bigint | null;
  result: bigint;
  squarings: SquaringRow[];
}

export interface SquaringRow {
  /** The power of two: a^(2^k). */
  exponent: bigint;
  value: bigint;
  used: boolean;
}

export const MAX_SQUARING_BITS = 64;

function squaringTable(a: bigint, b: bigint, n: bigint): SquaringRow[] {
  const rows: SquaringRow[] = [];
  let remaining = b;
  let power = BIG_ONE;
  let value = mod(a, n);
  while (remaining > BIG_ZERO && rows.length < MAX_SQUARING_BITS) {
    rows.push({
      exponent: power,
      value,
      used: remaining % BIG_TWO === BIG_ONE,
    });
    remaining /= BIG_TWO;
    power *= BIG_TWO;
    value = (value * value) % n;
  }
  return rows;
}

/** (a ○ b) mod n for ○ in +, −, ×, ^ with n ≥ 2; b ≥ 0 for powers. */
export function modularOperation(
  operation: ModOperation,
  a: bigint,
  b: bigint,
  n: bigint
): ModularResult | null {
  if (n < BIG_TWO || (operation === "power" && b < BIG_ZERO)) {
    return null;
  }
  const aReduced = mod(a, n);
  const bReduced = mod(b, n);
  let raw: bigint | null = null;
  let result = BIG_ZERO;
  if (operation === "add") {
    raw = a + b;
    result = mod(raw, n);
  } else if (operation === "subtract") {
    raw = a - b;
    result = mod(raw, n);
  } else if (operation === "multiply") {
    raw = a * b;
    result = mod(raw, n);
  } else {
    result = modPowBig(a, b, n);
  }
  return {
    operation,
    a,
    b,
    n,
    aReduced,
    bReduced,
    raw,
    result,
    squarings: operation === "power" ? squaringTable(a, b, n) : [],
  };
}

/* ------------------------------------------------------------------ table */

export type TableOperation = "add" | "multiply";
export const MAX_TABLE_MODULUS = 16;

/** The addition or multiplication table for the residues 0 … n − 1. */
export function modularTable(n: number, operation: TableOperation): number[][] {
  const rows: number[][] = [];
  for (let i = 0; i < n; i++) {
    const row: number[] = [];
    for (let j = 0; j < n; j++) {
      row.push(operation === "add" ? (i + j) % n : (i * j) % n);
    }
    rows.push(row);
  }
  return rows;
}
